import { Box, Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Activity, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AccordionSection } from '../common/AccordionSection';
import { EmptyState } from '../common/EmptyState';
import { VisitVitalsForm } from '../visits/VisitVitalsForm';
import { VisitVitalsHistoryTab } from '../visits/VisitVitalsHistoryTab';
import { Visit, Appointment } from '../../types';

interface PatientVisitHistorySectionProps {
  expanded: string | false;
  onExpandChange: (panel: string) => void;
  visits: Visit[];
  loading: boolean;
  appointment?: Appointment;
  showVitalsForm: boolean;
  setShowVitalsForm: (v: boolean) => void;
  currentVisitHasVitals: boolean;
  onLatestVitalsStatus?: (hasVitals: boolean) => void;
  canRecordReadings: boolean;
  loadPatientHistory: () => void;
  patientId: string;
}

export function PatientVisitHistorySection({
  expanded,
  onExpandChange,
  visits,
  loading,
  appointment,
  showVitalsForm,
  setShowVitalsForm,
  currentVisitHasVitals,
  onLatestVitalsStatus,
  canRecordReadings,
  loadPatientHistory,
  patientId,
}: PatientVisitHistorySectionProps) {
  const theme = useTheme();
  const navigate = useNavigate();

  const headerActions = (
    <>
      {canRecordReadings && !showVitalsForm && appointment?.status === 'IN_PROGRESS' && !currentVisitHasVitals && (
        <Button size="small" variant="outlined" startIcon={<Activity size={16} />}
          onClick={(e) => { e.stopPropagation(); setShowVitalsForm(true); }}
        >
          Add Vitals
        </Button>
      )}
      {appointment && (
        <Button size="small" variant="contained" color="success" startIcon={<DollarSign size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/billing?patientId=${patientId}&appointmentId=${appointment.id}`);
          }}
        >
          💳 Create Bill
        </Button>
      )}
    </>
  );

  return (
    <AccordionSection
      panelId="visit-history"
      expanded={expanded}
      onExpandChange={onExpandChange}
      icon={<Activity size={20} color={theme.palette.info.main} />}
      title="Visit History"
      count={visits.length}
      headerActions={headerActions}
      detailsSx={{ p: 0 }}
    >
      {showVitalsForm ? (
        <Box sx={{ p: 3 }}>
          <VisitVitalsForm
            patientId={patientId}
            onSuccess={() => { setShowVitalsForm(false); loadPatientHistory(); }}
            onCancel={() => setShowVitalsForm(false)}
          />
        </Box>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : visits.length > 0 ? (
        <Box sx={{ p: 2 }}>
          <VisitVitalsHistoryTab
            patientId={patientId}
            appointmentStatus={appointment?.status}
            onLatestVitalsStatus={onLatestVitalsStatus}
          />
        </Box>
      ) : (
        <EmptyState message="No visit history found" icon={<Activity size={40} />} />
      )}
    </AccordionSection>
  );
}
